import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Building2, Network } from "lucide-react";
import {
  autoAcceptEnquiryService,
  type AutoAcceptEnquiryConfig,
} from "@/services/autoAcceptEnquiryService";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { cn } from "@/lib/cn";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { InfoHint } from "@/components/atoms/InfoHint";
import { MenuSelect, type MenuSelectOption } from "@/components/molecules/MenuSelect";
import Dialog from "@/components/atoms/Dialog";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { flattenEntities, findEntityById } from "@/modules/settings/entityHelpers";
import {
  InfoCallout,
  OnOffLabel,
  SaveStatus,
} from "@/modules/settings/SettingsPrimitives";
import { SCOPE_BAR_CLASS, actionBarClass } from "@/modules/settings/settingsStyles";
import type { LegalEntityHierarchy } from "@/services/profileService";

interface Props {
  legalEntities: LegalEntityHierarchy[];
  /** Scope the form opens on — seeded from the app context, then owned locally. */
  initialLegalEntityId?: string | null;
  initialBranchId?: string | null;
}

/** Nothing saved for a scope means the automation is off. */
const DEFAULT_ENABLED = false;

/**
 * Auto-accept enquiries. Configured per legal entity or per branch: leaving the
 * branch select on "All Branches" writes the LEGAL_ENTITY-scoped row, picking a
 * branch writes that branch's own row. The selects belong to this form only and
 * never touch the global app context shown in the header.
 */
export function AutoAcceptEnquirySection({
  legalEntities,
  initialLegalEntityId,
  initialBranchId,
}: Props) {
  const allLegalEntities = useMemo(() => flattenEntities(legalEntities), [legalEntities]);

  // Form-local scope. Seeded once from the app context; never written back.
  const [legalEntityId, setLegalEntityId] = useState<string | null>(initialLegalEntityId ?? null);
  const [branchId, setBranchId] = useState<string | null>(initialBranchId ?? null);

  // Fall back to the first available legal entity when the seeded one isn't
  // part of the profile hierarchy (or nothing was seeded).
  useEffect(() => {
    if (allLegalEntities.length === 0) return;
    if (legalEntityId && allLegalEntities.some((le) => le.id === legalEntityId)) return;
    setLegalEntityId(allLegalEntities[0].id);
    setBranchId(null);
  }, [allLegalEntities, legalEntityId]);

  const [enabled, setEnabled] = useState(DEFAULT_ENABLED);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Whether this scope already has a row — only used to word the hint below. */
  const [hasSavedConfig, setHasSavedConfig] = useState(false);
  const [savedEnabled, setSavedEnabled] = useState(DEFAULT_ENABLED);

  // Read the config for the selected scope. A scope with nothing saved (204)
  // reads as the default rather than as a failure.
  useEffect(() => {
    if (!legalEntityId) {
      setEnabled(DEFAULT_ENABLED);
      setSavedEnabled(DEFAULT_ENABLED);
      setHasSavedConfig(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void autoAcceptEnquiryService
      .get({ legalEntityId, branchId })
      .then((config) => {
        if (cancelled) return;
        const next = config?.enabled ?? DEFAULT_ENABLED;
        setEnabled(next);
        setSavedEnabled(next);
        setHasSavedConfig(Boolean(config));
      })
      .catch((err) => {
        if (cancelled) return;
        setEnabled(DEFAULT_ENABLED);
        setSavedEnabled(DEFAULT_ENABLED);
        setHasSavedConfig(false);
        showActionErrorFromUnknown(err, "Failed to load the auto-accept enquiry setting.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [legalEntityId, branchId]);

  const isDirty = useMemo(() => enabled !== savedEnabled, [enabled, savedEnabled]);
  const unsavedGuard = useUnsavedChangesGuard(isDirty);

  function resetConfig() {
    setEnabled(savedEnabled);
  }

  async function saveConfig() {
    if (!legalEntityId) return;
    // "All Branches" is the legal-entity-wide row; a picked branch gets its own.
    const payload: AutoAcceptEnquiryConfig = {
      scope: branchId ? "BRANCH" : "LEGAL_ENTITY",
      legalEntityId,
      branchId: branchId ?? null,
      enabled,
    };
    try {
      setSaving(true);
      const saved = await autoAcceptEnquiryService.save(payload);
      const next = saved?.enabled ?? enabled;
      setEnabled(next);
      setSavedEnabled(next);
      setHasSavedConfig(true);
      toast.success("Auto-accept enquiry setting saved.");
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to save the auto-accept enquiry setting.");
    } finally {
      setSaving(false);
    }
  }

  const selectedLe = findEntityById(legalEntities, legalEntityId ?? "");
  const branches = selectedLe?.branches ?? [];

  const legalEntityOptions: MenuSelectOption[] = allLegalEntities.map((le) => ({
    value: le.id,
    label: le.name,
    subtitle: le.groupId,
  }));

  const branchOptions: MenuSelectOption[] = [
    { value: "", label: "All Branches" },
    ...branches.map((br) => ({
      value: br.id,
      label: br.name,
      subtitle: br.groupId,
    })),
  ];

  const saveReasons: string[] = [];
  if (!legalEntityId) saveReasons.push("Select a legal entity");
  if (!isDirty) saveReasons.push("No changes to save");
  const saveDisabled = saveReasons.length > 0;

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-bridge-accent border-t-transparent" />
        </div>
      )}

      {/* Scope selection — applies to this form only, not the app-wide context.
          Pinned to the top of the page scroll, like Viewing Settings. */}
      <div className={SCOPE_BAR_CLASS}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <div className="mb-2 flex items-center gap-2 text-bridge-muted">
              <Building2 size={16} className="text-bridge-accent" />
              <h4 className="text-sm font-bold uppercase tracking-wider">Legal Entity</h4>
              <InfoHint content="The legal entity whose auto-accept setting you want to edit. Changing it clears the selected branch." />
            </div>
            <MenuSelect
              hideLabel
              value={legalEntityId ?? ""}
              options={legalEntityOptions}
              placeholder="Select legal entity"
              searchable={legalEntityOptions.length > 6}
              searchPlaceholder="Search legal entities…"
              onChange={(val) =>
                unsavedGuard.confirm(() => {
                  setLegalEntityId(val);
                  setBranchId(null);
                })
              }
            />
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2 text-bridge-muted">
              <Network size={16} className="text-bridge-accent" />
              <h4 className="text-sm font-bold uppercase tracking-wider">Branch</h4>
              <InfoHint
                content={
                  legalEntityId
                    ? "Leave on All Branches to set this for the whole legal entity, or pick a branch to give it its own setting."
                    : "Select a legal entity first to list its branches."
                }
              />
            </div>
            <MenuSelect
              hideLabel
              value={branchId ?? ""}
              options={branchOptions}
              placeholder="All Branches"
              disabled={!legalEntityId}
              searchable={branches.length > 6}
              searchPlaceholder="Search branches…"
              onChange={(val) => unsavedGuard.confirm(() => setBranchId(val || null))}
            />
          </div>
        </div>
      </div>

      <div className="px-5 py-5">
        <div className="w-full lg:w-[70%]">
          {!legalEntityId ? (
            <div className="mb-4">
              <InfoCallout>Select a legal entity above to edit this setting.</InfoCallout>
            </div>
          ) : (
            <p className="mb-4 text-xs text-bridge-muted">
              {hasSavedConfig
                ? `Applies to ${branchId ? "this branch" : "this legal entity"}.`
                : `Nothing saved for ${branchId ? "this branch" : "this legal entity"} yet — saving creates it.`}
            </p>
          )}

          <div
            className={cn(
              "flex items-center justify-between rounded-lg border px-3 py-3 transition-colors",
              enabled ? "border-bridge-accent/25 bg-bridge-accent/5" : "border-bridge-border bg-white",
            )}
          >
            <span
              className={cn(
                "text-sm font-medium",
                legalEntityId ? "text-bridge-fg" : "text-bridge-muted",
              )}
            >
              Auto-accept enquiries
            </span>
            <div className="flex items-center gap-2.5">
            <OnOffLabel on={enabled} />
            <Tooltip
              content={
                legalEntityId
                  ? "On means incoming enquiries are accepted automatically for this scope. Off leaves them for manual acceptance."
                  : "Select a legal entity above to change this."
              }
              side="top"
            >
              {/* A disabled button never fires mouse events, so the hover target
                  has to be this wrapper, not the button itself. */}
              <span className={cn("inline-flex", !legalEntityId && "cursor-not-allowed")}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label="Auto-accept enquiries"
                  disabled={!legalEntityId}
                  onClick={() => setEnabled((prev) => !prev)}
                  className={cn(
                    "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-bridge-accent focus:ring-offset-2",
                    legalEntityId
                      ? "cursor-pointer"
                      : "pointer-events-none cursor-not-allowed opacity-50",
                    enabled ? "bg-bridge-accent" : "border border-bridge-border bg-bridge-surface",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "pointer-events-none inline-block h-4 w-4 transform rounded-full shadow ring-0 transition duration-200 ease-in-out",
                      enabled ? "translate-x-4 bg-white" : "translate-x-0 bg-bridge-muted",
                    )}
                  />
                </button>
              </span>
            </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Footer bar — the last child of the card, spanning it edge to edge, and
          docked to the bottom of the page scroll so Save is always in reach. */}
      <div className={actionBarClass(isDirty)}>
        <SaveStatus dirty={isDirty} />
        <span className="ml-auto" />
        <Button
          variant="outline"
          onClick={resetConfig}
          disabled={!isDirty || saving}
          title="Discard changes and restore the last saved value"
        >
          Reset
        </Button>
        <Tooltip
          content={
            saveReasons.length > 1
              ? saveReasons.map((r) => `• ${r}`).join("\n")
              : saveReasons.join("")
          }
          side="top"
        >
          <span className={saveDisabled ? "inline-flex cursor-not-allowed" : "inline-flex"}>
            <Button
              variant="primary"
              onClick={saveConfig}
              loading={saving}
              disabled={saveDisabled}
              className={saveDisabled ? "pointer-events-none" : undefined}
            >
              Save auto-accept setting
            </Button>
          </span>
        </Tooltip>
      </div>

      <Dialog
        open={unsavedGuard.open}
        title="Discard unsaved changes?"
        subtitle="Your change to the auto-accept enquiry setting has not been saved yet."
        submitLabel="Yes, discard"
        cancelLabel="No, stay here"
        // Both the X and the overlay mean "don't do it", same as No.
        onClose={unsavedGuard.cancel}
        onSubmit={unsavedGuard.proceed}
      >
        <p className="text-sm text-bridge-muted">
          Leaving this scope will lose the change you have made. Save it first if you want to keep
          it.
        </p>
      </Dialog>
    </div>
  );
}
